import { type SiteOrder, type SiteOrderLine } from '@api/vendor-portal';
import StatusPill from '@components/ui/StatusPill';
import { Button, Group, Progress, Text } from '@mantine/core';
import { CaretRightIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { Fragment, type FC, useState } from 'react';

import { ColumnHeader, NumberCell } from './CardFigures';
import { COLUMNS, type ColumnKey, formatDue, fromStockFor } from './cardColumns';
import { remaining } from './orderTotals';
import { useOrderDocument } from './useOrderDocument';

import styles from './JobCard.module.css';

interface JobCardProps {
    order: SiteOrder;
    /**
     * Finished goods per SKU at this site: `total` is what is here, `sendable` is what may
     * actually leave (usable plus in-QC, which is what a dispatch may draw from).
     */
    stockByProduct: Map<string, { total: number }>;
    isAcknowledging: boolean;
    onAcknowledge: () => void;
    /**
     * Record every SKU of this order at once, rather than one line at a time.
     *
     * The single Declare beside each line still handles the common case - one run finishing now.
     * This is for the order that was made as one batch, where declaring three lines separately
     * means typing the same code and dates three times.
     */
    onDeclareAll: (order: SiteOrder) => void;
    /** Declare against a run already started, with its code and remainder filled in. */
    /** Close a run that finished short, so the shortfall stops counting as in production. */
}

/**
 * One order, as a job rather than as a record.
 *
 * SEVEN FIGURES PER ROW, following the job from asked to gone: ordered, open, manufactured, QC
 * approved, QC pending, sent, to send. Each stage flows into the next — declaring raises
 * MANUFACTURED and lands in QC PENDING, a verdict moves it to QC APPROVED, sending drains either
 * into SENT — so the row reads as a pipeline rather than as seven unrelated counts. They are
 * named once, in the header strip at the top of the card, and every row below sits under those
 * names — the same names `SkuCard` uses, from the same `COLUMNS`.
 *
 * QC IS NOT A GATE ON THE TWO MIDDLE COLUMNS, which is why both say "still at the site" rather
 * than "releasable". Unruled goods may be dispatched — the brand inspects the load at its own
 * dock — so QC pending is a fact about paperwork, not about whether the lorry can leave.
 *
 * SENT IS READ-ONLY HERE, AND STILL BELONGS. The site cannot ship — the brand releases finished
 * goods once it has passed them — so this card offers no Send. The figure stays because the site
 * still needs to know what has gone against the job: without it, a run declared and then
 * collected reads as work that vanished.
 *
 * OVER-PRODUCTION IS SHOWN, NEVER SCOLDED. Real production over-plans for yield, and producing
 * to stock is a legitimate thing to do — so more declared than ordered colours differently and
 * says so, rather than being clamped to look like exactly enough.
 */
const JobCard: FC<JobCardProps> = ({
    order,
    stockByProduct,
    isAcknowledging,
    onAcknowledge,
    onDeclareAll,
}) => {
    const sum = (pick: (line: SiteOrderLine) => number) =>
        order.lines.reduce((total, line) => total + (pick(line) || 0), 0);

    const totalOrdered = sum((line) => line.ordered);
    const totalProduced = sum((line) => line.produced);
    const totalSent = sum((line) => line.dispatched);
    const totalReady = sum((line) => line.qcApproved);
    const totalAwaiting = sum((line) => line.qcPending);
    // Summed per line, so an over-shipped SKU cannot cancel out a short one.
    const totalOpen = sum((line) => remaining(line.ordered, line.produced ?? 0));
    const totalToSend = sum((line) => remaining(line.ordered, line.dispatched));
    /** What the order still owes, which is what a bulk declaration would be for. */
    const outstanding = Math.max(0, totalOrdered - totalProduced);

    const totals: Record<ColumnKey, number> = {
        ordered: totalOrdered,
        open: totalOpen,
        manufactured: totalProduced,
        toSend: totalToSend,
        qcApproved: totalReady,
        qcPending: totalAwaiting,
        sent: totalSent,
    };

    /*
     * COLLAPSED UNTIL ASKED. The card answers "how is this order going" at a glance; the SKUs
     * behind it are the follow-up question, and three of them open at once turned a list of
     * orders into a page nobody could scan.
     */
    const [isOpen, setIsOpen] = useState(false);
    const orderDocument = useOrderDocument();

    return (
        <div
            className={`${styles.card} ${order.needsAcknowledgement ? styles.unacknowledged : ''}`}
        >
            <ColumnHeader />

            <div className={styles.head}>
                {/*
                  * THE WHOLE HEAD IS THE DISCLOSURE, not a chevron somebody has to aim at. The
                  * order is the thing being opened, so its title is the thing to click.
                  */}
                <button
                    type="button"
                    className={styles.disclosure}
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen((open) => !open)}
                >
                    <CaretRightIcon
                        size={14}
                        weight="bold"
                        className={isOpen ? styles.caretOpen : styles.caret}
                    />
                    <div className={styles.title}>
                        <Group gap={8}>
                            <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                                {order.code}
                            </Text>
                            {/*
                              * THE ORDER'S OWN STATE, at the order and never on a line.
                              *
                              * On almost every card, because an order leaves this queue once it
                              * is fully sent — and that is the point rather than a fault. It is
                              * a state label like a due date, not an exception flag: the card
                              * says what this order IS, and "open" is what nearly all of them
                              * are. Neutral for exactly that reason — an always-present badge
                              * in a colour would drown the amber one beside it, which does mark
                              * an exception and does need to be seen.
                              *
                              * Read off the summed open quantity rather than a stored status,
                              * so it agrees with the Open column on the same row by
                              * construction. A partly-shipped order is still open.
                              */}
                            {totalOpen > 0 && <StatusPill label="Open" tone="neutral" />}
                            {order.needsAcknowledgement && (
                                <StatusPill label="Not acknowledged" tone="warning" />
                            )}
                        </Group>
                        <Text fz={12} c={neutralColors.textSecondary}>
                            {/* Placed-first is how a site decides what to make first, so the
                                order date leads. Absent on a headerless work order. */}
                            {order.orderDate
                                ? `Ordered ${formatDue(order.orderDate)} · `
                                : ''}
                            Due {formatDue(order.expectedCompletionDate)} ·{' '}
                            {order.lines.length} SKU{order.lines.length === 1 ? '' : 's'}
                        </Text>
                    </div>
                </button>

                {/*
                  * The order's figures, summed from its lines - the same six the SKUs show, so
                  * opening the card is a breakdown rather than a different set of words.
                  */}
                <div className={styles.numbers}>
                    {COLUMNS.map((column) => (
                        <NumberCell
                            key={column.key}
                            value={totals[column.key]}
                            muted={column.dimWhenZero && totals[column.key] === 0}
                            hint={column.hint}
                            fromStock={order.lines.reduce(
                                (acc, line) =>
                                    acc + fromStockFor(line.allocatedFromStock, column.key),
                                0,
                            )}
                        />
                    ))}
                </div>

                <div className={styles.actions}>
                    {/*
                     * The brand's own order document, where the work is.
                     *
                     * The email lands in one person's inbox; whoever is standing at the line
                     * asking what was actually ordered is usually not that person. Shown only
                     * when a document exists — a run commissioned without an order header has
                     * none, and offering a button that 404s is worse than offering nothing.
                     */}
                    {order.hasDocument && order.productionOrderId && (
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            size="xs"
                            loading={orderDocument.openingId === order.productionOrderId}
                            onClick={() => void orderDocument.open(order.productionOrderId)}
                        >
                            Order PDF
                        </Button>
                    )}

                    {/*
                      * Hidden once the order owes nothing. A button that opens a grid of zeroes
                      * invites declaring a run that was already recorded, and the card is
                      * already saying the order is made.
                      */}
                    {outstanding > 0 && (
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            size="xs"
                            onClick={() => onDeclareAll(order)}
                        >
                            Record production
                        </Button>
                    )}

                    {/* Shown whenever the badge is, because a badge nobody can clear is worse
                        than no badge. A run commissioned on its own has no order header, and
                        used to fall through this condition and sit unacknowledged forever. */}
                    {order.needsAcknowledgement && (
                        <Button
                            variant={ButtonVariant.ACTION}
                            size="xs"
                            loading={isAcknowledging}
                            onClick={onAcknowledge}
                        >
                            Acknowledge
                        </Button>
                    )}
                </div>
            </div>

            {isOpen && order.lines.map((line) => {
                const over = line.produced > line.ordered;

                /**
                 * Two segments, because two different things have happened.
                 *
                 * SENT is the order actually being answered — those goods are gone and the
                 * brand will receive them. READY is what has been made and not yet shipped:
                 * real progress, but progress the brand cannot bank yet.
                 *
                 * A bar drawn from `made` alone was wrong in both directions. It ignored stock
                 * shipped from an earlier run, which is why a line reading 280 sent and 140 made
                 * showed a bar barely started; and it counted goods still sitting on the floor
                 * as though the order were answered.
                 *
                 * Ready is what has been made BEYOND what has gone, so a unit made and then sent
                 * is counted once rather than filling the bar twice.
                 */
                const sentPct = line.ordered > 0
                    ? Math.min(100, (line.dispatched / line.ordered) * 100)
                    : 0;
                const readyPct = line.ordered > 0
                    ? Math.min(
                          100 - sentPct,
                          (Math.max(0, line.produced - line.dispatched) / line.ordered) * 100,
                      )
                    : 0;

                /*
                 * COALESCED, EVERY FIELD. A cell handed undefined renders NOTHING — not a zero,
                 * a gap — and a blank in a column of figures reads as "no data exists" when the
                 * truth is "this field did not arrive". The order row above already lands on 0
                 * through its own summing, so a missing field showed up as zeros on one row and
                 * holes on the next, which is a harder fault to recognise than either alone.
                 *
                 * Not theoretical: the frontend and the backend deploy separately, so a browser
                 * holding new code can talk to an older API for the length of a rollout, and
                 * these two fields were renamed. Reading 0 through that window is wrong for a
                 * few minutes; rendering gaps looks broken.
                 */
                const lineValues: Record<ColumnKey, number> = {
                    ordered: line.ordered ?? 0,
                    open: remaining(line.ordered, line.produced ?? 0),
                    manufactured: line.produced ?? 0,
                    toSend: remaining(line.ordered, line.dispatched),
                    qcApproved: line.qcApproved ?? 0,
                    qcPending: line.qcPending ?? 0,
                    sent: line.dispatched ?? 0,
                };

                return (
                    <Fragment key={line.workOrderId}>
                    <div className={styles.line}>
                        <div className={styles.title}>
                            <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                                {line.productName ?? line.productId}
                            </Text>
                            <Text fz={12} c={neutralColors.textMuted}>
                                due {formatDue(line.dueDate)}
                                {over && (
                                    // fz is set explicitly: a nested Text does not inherit its
                                    // parent's size, it falls back to the theme default.
                                    <Text component="span" fz={12} c={semanticColors.warning.dark}>
                                        {' '}
                                        · over by {line.produced - line.ordered}
                                    </Text>
                                )}
                            </Text>
                            <Progress.Root size="xs" w={220}>
                                <Progress.Section
                                    value={over ? 100 : sentPct}
                                    color={over ? 'yellow' : 'green'}
                                />
                                {!over && (
                                    // Lighter: made, but not yet gone.
                                    <Progress.Section value={readyPct} color="green.3" />
                                )}
                            </Progress.Root>
                        </div>

                        <div className={styles.numbers}>
                            {COLUMNS.map((column) => (
                                <NumberCell
                                    key={column.key}
                                    value={lineValues[column.key]}
                                    muted={column.dimWhenZero && lineValues[column.key] === 0}
                                    hint={column.hint}
                                    fromStock={fromStockFor(line.allocatedFromStock, column.key)}
                                />
                            ))}
                        </div>

                        <div className={styles.actions}>
                            {/* Only when the columns do NOT already tell this story: allocated
                                stock now shows inside Manufactured/QC, so the hint survives
                                only for stock that could not allocate (failed QC, expired). */}
                            {(stockByProduct.get(line.productId)?.total ?? 0) > 0
                                && !line.allocatedFromStock?.total && (
                                <Text fz={12} c={neutralColors.textMuted}>
                                    {stockByProduct.get(line.productId)?.total} in stock
                                </Text>
                            )}
                            {/* Kept, though it can no longer be asked for here: a line already
                                waiting on the brand should still say so. */}
                            {line.closeRequestedAt && (
                                <StatusPill label="Close requested" tone="info" />
                            )}
                        </div>
                    </div>

                    {/* NO PER-LINE DECLARE, AND NO RUN ROWS, on purpose: recording production
                        goes through the order's own grid - one modal, one set of rules. A run
                        in progress still counts in the columns above; the brand completes it
                        on the site's behalf. The capabilities stay in the codebase, unshown. */}
                    </Fragment>
                );
            })}
        </div>
    );
};

export default JobCard;
